import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildReasonsForVisitConfig } from "./configs/reasons-for-visit.config";

export interface ReasonForVisitResponse {
  message: ReasonForVisit[];
}

export interface ReasonForVisit {
  reason: string;
  display: string;
}

export interface ReasonsForVisitError {
  error: string;
}

let reasonsForVisitCache: ReasonForVisit[] | undefined;
let inflightReasonsForVisitRequest: Promise<ReasonForVisit[] | ReasonsForVisitError> | undefined;

export const clearReasonsForVisitCache = () => {
  reasonsForVisitCache = undefined;
  inflightReasonsForVisitRequest = undefined;
};

const normalizeReasonsForVisitResponse = (data: ReasonForVisitResponse): ReasonForVisit[] => {
  if (!Array.isArray(data.message)) {
    throw new Error("Failed to parse reasons for visit information.");
  }

  return data.message;
};

export const fetchReasonsForVisit = async (): Promise<ReasonForVisit[] | ReasonsForVisitError> => {
  if (reasonsForVisitCache) {
    return reasonsForVisitCache;
  }

  if (inflightReasonsForVisitRequest !== undefined) {
    return inflightReasonsForVisitRequest;
  }

  const requestPromise = (async (): Promise<ReasonForVisit[] | ReasonsForVisitError> => {
    try {
      const { data, response } = await callApi(buildReasonsForVisitConfig());

      if (!response.ok) {
        const errorMessage = `API responded with status ${response.status}: ${response.statusText}`;
        ClientLogsService.logError(
          "system",
          "reasons-for-visit.service.fetchReasonsForVisit",
          "Failed to fetch reasons for visit data",
          new Error(errorMessage),
          {
            errorType: getFailureTypeForHttpStatus(response.status),
            dependencyName: "ReasonsForVisitAPI",
            functionName: "fetchReasonsForVisit",
          },
        );

        return { error: errorMessage };
      }

      const reasonsForVisit = normalizeReasonsForVisitResponse(data as ReasonForVisitResponse);
      reasonsForVisitCache = reasonsForVisit;

      return reasonsForVisit;
    } catch (error: unknown) {
      ClientLogsService.logError(
        "system",
        "reasons-for-visit.service.fetchReasonsForVisit",
        "Error fetching reasons for visit data",
        error instanceof Error ? error : new Error("Unknown error"),
        {
          errorType: "DependencyFailure",
          dependencyName: "ReasonsForVisitAPI",
          functionName: "fetchReasonsForVisit",
        },
      );

      const errorMessage =
        error instanceof Error ? error.message || "Unknown error" : "Unknown error";

      return { error: errorMessage };
    } finally {
      inflightReasonsForVisitRequest = undefined;
    }
  })();

  inflightReasonsForVisitRequest = requestPromise;

  return requestPromise;
};

export default { fetchReasonsForVisit, clearReasonsForVisitCache };
